import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';

import { useInfiniteScrollSentinel } from '~/ui-rocco/pages/category-page/hooks/useInfiniteScrollSentinel';

interface UseProfessionalHybridLoadParams {
    /** More pages exist beyond what's shown (from the tab-load engine). */
    hasMore: boolean;
    /** A page fetch is in flight. */
    isLoading: boolean;
    /** Fetch + append the next page. */
    loadMore: () => void;
    /**
     * Changes whenever the result set resets to page one — the active filter /
     * search querystring. Flips the tab back to the View-More button so a new
     * result set starts paged rather than mid-infinite-scroll.
     */
    resetKey: string;
}

interface UseProfessionalHybridLoadReturn {
    /** True once the user has opted into infinite scroll for this result set. */
    infiniteEnabled: boolean;
    /** Show the "View more" button — more pages exist and infinite isn't on yet. */
    showViewMore: boolean;
    /** View-More click: load the next page and flip into infinite scroll. */
    onViewMore: () => void;
    /** Sentinel ref — only fires while infinite scroll is enabled. */
    sentinelRef: (node: HTMLElement | null) => void;
}

/**
 * Hybrid pagination for the professional grid tabs, mirroring the category
 * page's DirectoryUniformGrid: the first page loads, a "View more" button
 * appears, and clicking it loads the next page *and* flips the tab into infinite
 * scroll for the rest. Resets back to the button whenever the result set resets
 * (a filter / search change, keyed by `resetKey`). Sits on top of the shared
 * professional tab-load engine ({@link import('./use-tab-load-more').useTabLoadMore}),
 * so every grid tab shares one paging behaviour.
 */
export const useProfessionalHybridLoad = ({
    hasMore,
    isLoading,
    loadMore,
    resetKey,
}: UseProfessionalHybridLoadParams): UseProfessionalHybridLoadReturn => {
    const [infiniteEnabled, setInfiniteEnabled] = useState(false);

    // A new result set (filter / search change) starts paged again. Legacy tab
    // sort/filters live in the pathname as segments (sort_featured, category_*),
    // not just the query string, so fold the pathname into the reset key —
    // otherwise a path-only filter change would leave infinite scroll enabled and
    // skip the View more button on the fresh result set.
    const { pathname } = useLocation();
    const effectiveResetKey = `${pathname}?${resetKey}`;
    const resetKeyRef = useRef(effectiveResetKey);
    useEffect(() => {
        if (resetKeyRef.current === effectiveResetKey) return;
        resetKeyRef.current = effectiveResetKey;
        setInfiniteEnabled(false);
    }, [effectiveResetKey]);

    const sentinelRef = useInfiniteScrollSentinel({
        enabled: infiniteEnabled && hasMore && !isLoading,
        onTrigger: loadMore,
    });

    const onViewMore = useCallback(() => {
        setInfiniteEnabled(true);
        loadMore();
    }, [loadMore]);

    return {
        infiniteEnabled,
        showViewMore: hasMore && !infiniteEnabled,
        onViewMore,
        sentinelRef,
    };
};
